import { supabase } from '@/integrations/supabase/client';

export interface ClientTeamMember {
  id: string;
  client_id: string;
  user_id?: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  invited_at: string;
  joined_at?: string;
  invited_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ClientTeamInvitation {
  id: string;
  client_id: string;
  email: string;
  name: string;
  role: string;
  invitation_token: string;
  invited_by: string;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
  clients?: {
    name: string;
    company?: string;
  };
}

export interface CreateInvitationData {
  client_id: string;
  email: string;
  name: string;
  role?: string;
}

export const clientTeamService = {
  // Get team members for a client
  async getClientTeamMembers(clientId: string): Promise<ClientTeamMember[]> {
    const { data, error } = await supabase
      .from('client_team_members')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Invite team member
  async inviteTeamMember(invitationData: CreateInvitationData): Promise<ClientTeamInvitation> {
    const { data, error } = await supabase
      .from('client_team_invitations')
      .insert({
        client_id: invitationData.client_id,
        email: invitationData.email,
        name: invitationData.name,
        role: invitationData.role || 'member',
        invited_by: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get invitation by token (no expiry check for team member invitations)
  async getInvitationByToken(token: string): Promise<ClientTeamInvitation | null> {
    const { data, error } = await supabase
      .from('client_team_invitations')
      .select(`
        *,
        clients:client_id (
          name,
          company
        )
      `)
      .eq('invitation_token', token)
      .is('accepted_at', null)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows found
      throw error;
    }
    return data;
  },

  // Accept invitation and join team
  async acceptInvitation(token: string, userId: string): Promise<ClientTeamMember> {
    // Get invitation details
    const invitation = await this.getInvitationByToken(token);
    if (!invitation) {
      throw new Error('Invalid or expired invitation');
    }

    // Check if user is already a team member for this client
    const existingMember = await this.isUserTeamMember(invitation.client_id, userId);
    if (existingMember) {
      // Just mark invitation as accepted and return existing membership
      await supabase
        .from('client_team_invitations')
        .update({ accepted_at: new Date().toISOString() })
        .eq('invitation_token', token);
      return existingMember;
    }

    // Create new team member
    const { data: teamMember, error: memberError } = await supabase
      .from('client_team_members')
      .insert({
        client_id: invitation.client_id,
        user_id: userId,
        email: invitation.email,
        name: invitation.name,
        role: invitation.role,
        joined_at: new Date().toISOString(),
        invited_by: invitation.invited_by
      })
      .select()
      .single();

    if (memberError) throw memberError;

    // Mark invitation as accepted
    await supabase
      .from('client_team_invitations')
      .update({ accepted_at: new Date().toISOString() })
      .eq('invitation_token', token);

    // Send notification email to inviter
    const clientName = invitation.clients?.name || 'Unknown Client';
    try {
      await supabase.functions.invoke('send-invitation-response', {
        body: {
          invitedByUserId: invitation.invited_by,
          memberName: invitation.name,
          memberEmail: invitation.email,
          clientName,
          responseType: 'accepted'
        }
      });
    } catch (emailError) {
      console.error('Failed to send acceptance notification:', emailError);
      // Don't throw - the invitation was accepted successfully
    }

    return teamMember;
  },

  // Get current user's team memberships
  async getCurrentUserMemberships(): Promise<ClientTeamMember[]> {
    const { data, error } = await supabase
      .from('client_team_members')
      .select(`
        *,
        clients:client_id (
          name,
          company
        )
      `)
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
      .eq('is_active', true);

    if (error) throw error;
    return data || [];
  },

  // Update team member status
  async updateTeamMemberStatus(memberId: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('client_team_members')
      .update({ is_active: isActive })
      .eq('id', memberId);

    if (error) throw error;
  },

  // Remove team member
  async removeTeamMember(memberId: string): Promise<void> {
    const { error } = await supabase
      .from('client_team_members')
      .delete()
      .eq('id', memberId);

    if (error) throw error;
  },

  // Check if user is team member of client
  async isUserTeamMember(clientId: string, userId?: string): Promise<ClientTeamMember | null> {
    if (!userId) return null;

    const { data, error } = await supabase
      .from('client_team_members')
      .select('*')
      .eq('client_id', clientId)
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows found
      throw error;
    }
    return data;
  },

  // Get pending invitations for current user's email (no expiry check for team member invitations)
  async getPendingInvitationsForEmail(email: string): Promise<ClientTeamInvitation[]> {
    const { data: user } = await supabase.auth.getUser();
    const userId = user?.user?.id;

    // First get existing memberships to exclude
    let existingMemberClientIds: string[] = [];
    if (userId) {
      const { data: memberships } = await supabase
        .from('client_team_members')
        .select('client_id')
        .eq('user_id', userId)
        .eq('is_active', true);
      existingMemberClientIds = memberships?.map(m => m.client_id) || [];
    }

    // Build query with case-insensitive email matching (no expiry check)
    let query = supabase
      .from('client_team_invitations')
      .select(`
        *,
        clients:client_id (
          name,
          company
        )
      `)
      .ilike('email', email)
      .is('accepted_at', null)
      .order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    // Filter out invitations for clients where user is already a member
    const filteredData = (data || []).filter(
      inv => !existingMemberClientIds.includes(inv.client_id)
    );

    return filteredData;
  },

  // Decline invitation
  async declineInvitation(token: string): Promise<void> {
    // Get invitation details before deleting
    const invitation = await this.getInvitationByToken(token);
    
    const { error } = await supabase
      .from('client_team_invitations')
      .delete()
      .eq('invitation_token', token);

    if (error) throw error;

    // Send notification email to inviter
    if (invitation) {
      const clientName = invitation.clients?.name || 'Unknown Client';
      try {
        await supabase.functions.invoke('send-invitation-response', {
          body: {
            invitedByUserId: invitation.invited_by,
            memberName: invitation.name,
            memberEmail: invitation.email,
            clientName,
            responseType: 'declined'
          }
        });
      } catch (emailError) {
        console.error('Failed to send decline notification:', emailError);
        // Don't throw - the invitation was declined successfully
      }
    }
  }
};
